import { BidBattleCard } from "@/components/battle";
import { m } from "@/paraglide/messages";
import { Button } from "@bidbattles/bloks/components/ui/button";
import { Input } from "@bidbattles/bloks/components/ui/input";
import { Link } from "@bidbattles/bloks/components/ui/link";
import { PageLoader } from "@bidbattles/bloks/components/ui/loading";
import { BBBackendInfiniteQueryOptions } from "@bidbattles/bloks/lib/rapace";
import { paginationSearchParamsSchema } from "@bidbattles/bloks/lib/rapace/utils";
import { cn } from "@bidbattles/bloks/lib/ui";
import { useDebouncedValue } from "@tanstack/react-pacer/debouncer";
import { useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowDown, SearchMd } from "@untitledui/icons";
import { useInView } from "motion/react";
import { Suspense, useEffect, useMemo, useRef } from "react";

export const Route = createFileRoute("/battles")({
	validateSearch: paginationSearchParamsSchema.pick({ query: true }),
	loaderDeps: ({ search: { query } }) => ({ query }),
	async loader({ context, deps: { query } }) {
		context.queryClient.prefetchInfiniteQuery(
			BBBackendInfiniteQueryOptions.listBattles({
				limit: 12,
				query: query,
			}),
		);
	},
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const { query } = Route.useSearch();
	const [debouncedQuery] = useDebouncedValue(query, {
		wait: 100,
	});

	return (
		<main className="flex flex-col items-stretch grow h-full bg-gray-warm-100">
			<section className="bg-white flex flex-col items-center gap-y-6 md:gap-y-8">
				<div className="p-box max-w-100 md:max-w-150 mx-auto flex flex-col items-center gap-y-3 text-center">
					<h1 className="text-gray-900 font-bold text-5xl leading-11 md:text-6xl md:leading-22.5">
						{m.home__hero__title_part_1()}
						<br />
						{m.home__hero__title_part_2()}
					</h1>
					<p className="text-gray-500 text-lg leading-7 mb-4">
						{m.home__hero__subtitle_2()}
					</p>
					<Input
						className="max-w-99 bg-gray-warm-100 border-gray-warm-100"
						variant="roundedBox"
						prefix={<SearchMd size="1em" color="currentColor" />}
						value={query || ""}
						onChange={(e) => {
							navigate({
								to: ".",
								search: { query: e.target.value },
								replace: true,
							});
						}}
					/>
				</div>
			</section>
			<section className="w-full p-box max-w-container mx-auto">
				<Suspense fallback={<PageLoader />}>
					<BattleList query={debouncedQuery} />
				</Suspense>
			</section>
		</main>
	);
}

function BattleList({ query }: { query: string | undefined }) {
	const {
		data: { pages },
		hasNextPage,
		fetchNextPage,
		isFetchingNextPage,
	} = useSuspenseInfiniteQuery({
		...BBBackendInfiniteQueryOptions.listBattles({
			limit: 12,
			query,
		}),
	});
	const battles = useMemo(() => pages.flatMap((page) => page.battles), [pages]);

	const ref = useRef(null);
	const inView = useInView(ref);
	useEffect(() => {
		if (inView && hasNextPage && !isFetchingNextPage) {
			fetchNextPage();
		}
	}, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

	return (
		<>
			<div className="flex flex-wrap gap-8 mb-6 justify-center">
				{battles.length === 0 && (
					<p className="text-gray-500">No results found for "{query}".</p>
				)}
				{battles.map((battle) => (
					<BidBattleCard
						key={battle.id}
						className={cn("basis-1/1 sm:basis-1/3 md:basis-1/4 md:max-w-70")}
						battle={battle}
						render={(props) => (
							<Link
								to="/battle/$id/details"
								params={{ id: battle.id }}
								{...props}
							/>
						)}
					/>
				))}
			</div>
			<div ref={ref} className="flex justify-center items-center gap-4 w-full">
				{battles.length > 0 &&
					(isFetchingNextPage ? (
						<PageLoader />
					) : (
						<Button
							variant="white"
							disabled={!hasNextPage || isFetchingNextPage}
							onClick={() => fetchNextPage()}
						>
							<ArrowDown size="1em" />
							See more
						</Button>
					))}
			</div>
		</>
	);
}
